import polyline from '@mapbox/polyline'
import { Run } from '../types'

const haversineDistance = (
  [lon1, lat1]: number[],
  [lon2, lat2]: number[]
): number => {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// A "jump" is the long straight artifact left when a watch is paused and
// resumed somewhere else, or when GPS drops out — we hide it by splitting the
// path there. summary_polyline is geometrically simplified, so ordinary
// straight roads also collapse to two far-apart points; scaling the threshold
// by speed or by the activity's own point spacing cut those real straightaways.
// Across the full activity history the largest *normal* gap tops out around
// ~375m (p90) / ~590m (p95), while real jumps run 600m to several km. So we
// split only on gaps past a flat, conservative threshold that sits just above
// that normal range. (Tune JUMP_THRESHOLD_METERS to cut more/less aggressively.)
const JUMP_THRESHOLD_METERS = 600

const splitLineString = (coords: number[][]): number[][][] => {
  if (coords.length < 2) {
    return []
  }
  const segments: number[][][] = []
  let current: number[][] = [coords[0]]
  for (let i = 1; i < coords.length; i++) {
    if (haversineDistance(coords[i - 1], coords[i]) > JUMP_THRESHOLD_METERS) {
      segments.push(current)
      current = []
    }
    current.push(coords[i])
  }
  segments.push(current)
  return segments.filter(s => s.length >= 2)
}

export const activityToFeature = (activity: Run) => {
  if (!activity.map) {
    return {
      id: activity.id,
      type: 'Feature',
      geometry: { type: 'MultiLineString', coordinates: [] },
      properties: activity,
    } as GeoJSON.Feature<GeoJSON.MultiLineString, Run>
  }
  const decoded = polyline.toGeoJSON(activity.map.summary_polyline)
  const segments = splitLineString(decoded.coordinates)
  return {
    id: activity.id,
    type: 'Feature',
    geometry:
      segments.length === 1
        ? { type: 'LineString', coordinates: segments[0] }
        : { type: 'MultiLineString', coordinates: segments },
    properties: activity,
  } as GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString, Run>
}

// Returns a representative midpoint to anchor the run's popup on. The geometry
// may be a LineString (number[][]) or a MultiLineString (number[][][], when the
// run was split at jumps), so flatten to a single list of points before picking
// the middle one. Returns null when there is no geometry to anchor to.
export const featureMidpoint = (
  feature: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString, Run>
): [number, number] | null => {
  const points =
    feature.geometry?.type === 'MultiLineString'
      ? (feature.geometry.coordinates as number[][][]).flat()
      : (feature.geometry?.coordinates as number[][]) ?? []
  if (!points.length) {
    return null
  }
  return points[Math.floor(points.length / 2)] as [number, number]
}
